import { z } from "zod"
import { WorkerRole } from "../auth/WorkerRole"
import { emailSchema } from "../common/email-validator"
import { TranslationKey } from "../i18n/translation-key"

export const StoreAccountRoleSchema = z.nativeEnum(WorkerRole)

export const CreateStoreAccountSchema = z.object({
  email: emailSchema(),
  firstName: z
    .string()
    .trim()
    .min(1, "common.validation.firstNameRequired" satisfies TranslationKey)
    .max(255),
  lastName: z
    .string()
    .trim()
    .min(1, "common.validation.lastNameRequired" satisfies TranslationKey)
    .max(255),
  role: StoreAccountRoleSchema,
})

export const UpdateStoreAccountSchema = CreateStoreAccountSchema.omit({
  email: true,
})

export const LinkStoreAccountSchema = z.object({
  mechanicId: z.guid(),
  userId: z.guid(),
})

// Re-points an existing link. Exactly one side changes; the replaced side
// becomes unlinked again. Clearing a link is intentionally not possible.
export const ChangeStoreAccountLinkSchema = z.union([
  z.object({ userId: z.guid() }),
  z.object({ newMechanicId: z.guid() }),
])

export type StoreAccountRole = z.infer<typeof StoreAccountRoleSchema>
export type CreateStoreAccount = z.infer<typeof CreateStoreAccountSchema>
export type UpdateStoreAccount = z.infer<typeof UpdateStoreAccountSchema>
export type LinkStoreAccount = z.infer<typeof LinkStoreAccountSchema>
export type ChangeStoreAccountLink = z.infer<
  typeof ChangeStoreAccountLinkSchema
>

export interface StoreAccountUser {
  id: string
  firstName: string
  lastName: string
  email: string
}

export interface LinkedStoreAccount {
  mechanicId: string
  name: string
  user: StoreAccountUser
  role: StoreAccountRole
  inactivatedAt: Date | null
}

export interface UnlinkedStoreMechanic {
  id: string
  name: string
}

export interface UnlinkedStoreAccount {
  user: StoreAccountUser
  role: StoreAccountRole
}

export interface StoreAccountLinkSuggestion {
  mechanicId: string
  userId: string
}

export interface StoreAccountOverview {
  linked: LinkedStoreAccount[]
  unlinkedMechanics: UnlinkedStoreMechanic[]
  unlinkedAccounts: UnlinkedStoreAccount[]
  suggestions: StoreAccountLinkSuggestion[]
}
